<script lang="ts">
  import CodeBlock from './CodeBlock.svelte'
  import MermaidBlock from './MermaidBlock.svelte'

  const { content = '', mentions = [] } = $props<{ content: string; mentions?: any[] }>()

  interface ParsedBlock {
    type: 'text' | 'code' | 'mermaid' | 'table'
    content: string
    language?: string
  }

  const mentionPlaceholders = $derived.by(() => {
    if (!mentions?.length) return { text: content, map: new Map<string, string>() }
    let text = content
    const map = new Map<string, string>()
    const sorted = [...mentions].sort((a: any, b: any) => b.offset - a.offset)
    for (let i = 0; i < sorted.length; i++) {
      const m = sorted[i]
      const placeholder = `\x00M${i}\x00`
      const span = text.slice(m.offset, m.offset + m.length)
      const isHighlight = m.type === 'all'
      const cls = isHighlight ? 'mention-hl mention-hl-me' : 'mention-hl'
      const escaped = span.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      map.set(placeholder, `<span class="${cls}">${escaped}</span>`)
      text = text.slice(0, m.offset) + placeholder + text.slice(m.offset + m.length)
    }
    return { text, map }
  })

  function restoreMentions(html: string): string {
    const { map } = mentionPlaceholders
    if (!map.size) return html
    for (const [placeholder, replacement] of map) {
      const escaped = placeholder.replace(/\x00/g, '\\x00')
      const escapedVariants = [placeholder, escaped,
        placeholder.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')]
      for (const v of escapedVariants) {
        if (html.includes(v)) {
          html = html.replace(v, replacement)
        }
      }
    }
    return html
  }

  const blocks = $derived.by(() => {
    const result: ParsedBlock[] = []
    const src = mentionPlaceholders.text
    const codeBlockRe = /```(\w*)\n([\s\S]*?)```/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = codeBlockRe.exec(src)) !== null) {
      if (match.index > lastIndex) {
        pushTextBlocks(result, src.slice(lastIndex, match.index))
      }
      const lang = match[1] || ''
      const code = match[2].replace(/\n$/, '')
      if (lang === 'mermaid') {
        result.push({ type: 'mermaid', content: code })
      } else {
        result.push({ type: 'code', content: code, language: lang })
      }
      lastIndex = match.index + match[0].length
    }

    if (lastIndex < src.length) {
      pushTextBlocks(result, src.slice(lastIndex))
    }

    return result
  })

  function pushTextBlocks(result: ParsedBlock[], text: string) {
    const tableRe = /(?:^|\n)(\|.+\|[ \t]*\n\|[\s:|-]+\|[ \t]*\n(?:\|.+\|[ \t]*\n?)+)/g
    let lastIdx = 0
    let m: RegExpExecArray | null

    while ((m = tableRe.exec(text)) !== null) {
      const before = text.slice(lastIdx, m.index)
      if (before.trim()) result.push({ type: 'text', content: before })
      result.push({ type: 'table', content: m[1].trim() })
      lastIdx = m.index + m[0].length
    }
    const after = text.slice(lastIdx)
    if (after.trim()) result.push({ type: 'text', content: after })
  }

  function renderTable(raw: string): string {
    const lines = raw.split('\n').filter(l => l.trim())
    if (lines.length < 2) return renderInline(raw)

    const parseRow = (line: string) =>
      line.split('|').slice(1, -1).map(c => c.trim())

    const headers = parseRow(lines[0])
    const alignLine = parseRow(lines[1])
    const aligns = alignLine.map(c => {
      if (c.startsWith(':') && c.endsWith(':')) return 'center'
      if (c.endsWith(':')) return 'right'
      return 'left'
    })
    const rows = lines.slice(2).map(parseRow)

    let html = '<div class="overflow-x-auto my-3"><table class="w-full text-sm border-collapse">'
    html += '<thead><tr>'
    headers.forEach((h, i) => {
      html += `<th class="px-3 py-2 border border-edge bg-surface-alt font-semibold text-left" style="text-align:${aligns[i] || 'left'}">${renderInline(h)}</th>`
    })
    html += '</tr></thead><tbody>'
    rows.forEach(row => {
      html += '<tr>'
      row.forEach((cell, i) => {
        html += `<td class="px-3 py-1.5 border border-edge" style="text-align:${aligns[i] || 'left'}">${renderInline(cell)}</td>`
      })
      html += '</tr>'
    })
    html += '</tbody></table></div>'
    return html
  }

  function renderInline(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
      .replace(/~~(.+?)~~/g, '<del class="opacity-60">$1</del>')
      .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-surface-alt border border-edge/50 rounded-md text-[0.85em] font-mono text-foreground">$1</code>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-primary hover:underline">$1</a>')
      // Autolink bare URLs (not already inside an href="...")
      .replace(/(?<!href="|">)(https?:\/\/[^\s<)"]+)/g, '<a href="$1" target="_blank" rel="noopener" class="text-primary hover:underline break-all">$1</a>')
  }

  function renderTextBlock(text: string): string {
    const lines = text.split('\n')
    const htmlParts: string[] = []
    let inUl = false
    let inOl = false
    let inBlockquote = false

    for (const line of lines) {
      const trimmed = line.trim()

      if (!trimmed) {
        if (inUl) { htmlParts.push('</ul>'); inUl = false }
        if (inOl) { htmlParts.push('</ol>'); inOl = false }
        if (inBlockquote) { htmlParts.push('</blockquote>'); inBlockquote = false }
        htmlParts.push('<div class="h-3"></div>')
        continue
      }

      if (/^---+$|^\*\*\*+$|^___+$/.test(trimmed)) {
        if (inUl) { htmlParts.push('</ul>'); inUl = false }
        if (inOl) { htmlParts.push('</ol>'); inOl = false }
        if (inBlockquote) { htmlParts.push('</blockquote>'); inBlockquote = false }
        htmlParts.push('<hr class="border-t border-edge my-4" />')
        continue
      }

      const bqMatch = trimmed.match(/^>\s?(.*)$/)
      if (bqMatch) {
        if (inUl) { htmlParts.push('</ul>'); inUl = false }
        if (inOl) { htmlParts.push('</ol>'); inOl = false }
        if (!inBlockquote) {
          htmlParts.push('<blockquote class="border-l-4 border-primary/40 pl-4 my-2 text-muted italic">')
          inBlockquote = true
        }
        htmlParts.push(`<p>${renderInline(bqMatch[1])}</p>`)
        continue
      }
      if (inBlockquote) { htmlParts.push('</blockquote>'); inBlockquote = false }

      const headerMatch = trimmed.match(/^(#{1,4})\s+(.+)$/)
      if (headerMatch) {
        if (inUl) { htmlParts.push('</ul>'); inUl = false }
        if (inOl) { htmlParts.push('</ol>'); inOl = false }
        const level = headerMatch[1].length
        const sizes: Record<number, string> = {
          1: 'text-xl font-bold mt-4 mb-2 tracking-tight',
          2: 'text-lg font-semibold mt-3 mb-1.5 tracking-tight',
          3: 'text-base font-semibold mt-3 mb-1',
          4: 'text-sm font-semibold mt-2 mb-1',
        }
        const tag = `h${level}`
        htmlParts.push(`<${tag} class="${sizes[level]}">${renderInline(headerMatch[2])}</${tag}>`)
        continue
      }

      const ulMatch = trimmed.match(/^[-*]\s+(.+)$/)
      if (ulMatch) {
        if (inOl) { htmlParts.push('</ol>'); inOl = false }
        if (!inUl) { htmlParts.push('<ul class="list-disc pl-5 space-y-0.5">'); inUl = true }
        htmlParts.push(`<li>${renderInline(ulMatch[1])}</li>`)
        continue
      }

      const olMatch = trimmed.match(/^\d+\.\s+(.+)$/)
      if (olMatch) {
        if (inUl) { htmlParts.push('</ul>'); inUl = false }
        if (!inOl) { htmlParts.push('<ol class="list-decimal pl-5 space-y-0.5">'); inOl = true }
        htmlParts.push(`<li>${renderInline(olMatch[1])}</li>`)
        continue
      }

      if (inUl) { htmlParts.push('</ul>'); inUl = false }
      if (inOl) { htmlParts.push('</ol>'); inOl = false }
      htmlParts.push(`<p>${renderInline(trimmed)}</p>`)
    }

    if (inUl) htmlParts.push('</ul>')
    if (inOl) htmlParts.push('</ol>')
    if (inBlockquote) htmlParts.push('</blockquote>')
    return htmlParts.join('\n')
  }
</script>

<div class="text-sm leading-relaxed text-foreground space-y-1 break-words">
  {#each blocks as block}
    {#if block.type === 'code'}
      <CodeBlock code={block.content} language={block.language} />
    {:else if block.type === 'mermaid'}
      <MermaidBlock definition={block.content} />
    {:else if block.type === 'table'}
      {@html restoreMentions(renderTable(block.content))}
    {:else}
      {@html restoreMentions(renderTextBlock(block.content))}
    {/if}
  {/each}
</div>
